import { forwardRef } from "react";
import clsx from "clsx";

import type { FormSelectProps } from "./formSelect.types";
import {
  getFormSelectContainerClasses,
  getFormSelectLabelClasses,
  getFormSelectClasses,
  getSelectErrorMessageClasses,
  getSelectHelperTextClasses,
} from "./formSelect.definitions";

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      label,
      error,
      required = false,
      options,
      placeholder,
      helperText,
      className,
      selectClassName,
      id,
      ...props
    },
    ref
  ) => {
    const selectId =
      id || `form-select-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = Boolean(error);

    return (
      <div className={getFormSelectContainerClasses(className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={getFormSelectLabelClasses(required, hasError)}
          >
            {label}
          </label>
        )}

        {/* Select */}
        <select
          ref={ref}
          id={selectId}
          className={clsx(getFormSelectClasses(hasError), selectClassName)}
          aria-invalid={hasError}
          aria-describedby={
            error
              ? `${selectId}-error`
              : helperText
              ? `${selectId}-helper`
              : undefined
          }
          {...props}
        >
          {/* Placeholder Option */}
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}

          {/* Options */}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Mensagem de Erro */}
        {error && (
          <p
            id={`${selectId}-error`}
            className={getSelectErrorMessageClasses()}
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Texto de Ajuda */}
        {helperText && !error && (
          <p id={`${selectId}-helper`} className={getSelectHelperTextClasses()}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormSelect.displayName = "FormSelect";

export default FormSelect;
